import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  ArticoloEscluso,
  ClientiMerceFermaResponse,
  M1DashboardKPIs,
  M1LeadTime,
  M1OrdineDiagnosi,
  M1OrdiniListResponse,
  M1SegnaleArticoloCritico,
  M1SegnaleStockEccedente,
  M1SpedibiliResponse,
  RitiroApplicato,
  RitiroUpdate,
  StatoRitiro,
} from "@/types/api";

export type OrdiniOrderBy =
  | "data_consegna"
  | "numero_ordine"
  | "cliente"
  | "n_righe"
  | "qty_residua"
  | "stato_maitime"
  | "stato_gestionale";

export type OrdiniOrderDir = "asc" | "desc";

export interface UseOrdiniOptions {
  stato?: string[];
  stato_maitime?: string[];
  /**
   * Lo stato **effettivo** di ritiro. Esiste lato server dal 18-08: prima il
   * riquadro «A chiamata» del Cruscotto navigava a `?ritiro=a_chiamata` e nessuno
   * lo leggeva — era inerte solo perché il conteggio era 0.
   */
  stato_ritiro?: StatoRitiro[];
  /** Solo le tassative con la data di ritiro già passata: la porta del Cruscotto
   *  conta 12 e deve trovarne 12, non le 17 tassative. */
  ritiro_scaduto?: boolean;
  /** Le commesse di solo «materiale di consumo»: non hanno stato di evadibilità,
   *  quindi nessun altro filtro le prende. */
  non_valutate?: boolean;
  solo_divergenze?: boolean;
  cliente_codice?: number;
  q?: string;
  data_consegna_da?: string;
  data_consegna_a?: string;
  solo_aperti?: boolean;
  order_by?: OrdiniOrderBy;
  order_dir?: OrdiniOrderDir;
  limit?: number;
  offset?: number;
}

function buildOrdiniQuery(opts: UseOrdiniOptions): string {
  const params = new URLSearchParams();
  for (const s of opts.stato ?? []) params.append("stato", s);
  for (const s of opts.stato_maitime ?? []) params.append("stato_maitime", s);
  for (const s of opts.stato_ritiro ?? []) params.append("stato_ritiro", s);
  if (opts.ritiro_scaduto) params.set("ritiro_scaduto", "true");
  if (opts.non_valutate) params.set("non_valutate", "true");
  if (opts.solo_divergenze) params.set("solo_divergenze", "true");
  if (opts.cliente_codice !== undefined) params.set("cliente_codice", String(opts.cliente_codice));
  if (opts.q) params.set("q", opts.q);
  if (opts.data_consegna_da) params.set("data_consegna_da", opts.data_consegna_da);
  if (opts.data_consegna_a) params.set("data_consegna_a", opts.data_consegna_a);
  if (opts.solo_aperti) params.set("solo_aperti", "true");
  if (opts.order_by) params.set("order_by", opts.order_by);
  if (opts.order_dir) params.set("order_dir", opts.order_dir);
  params.set("limit", String(opts.limit ?? 50));
  params.set("offset", String(opts.offset ?? 0));
  return params.toString();
}

export function useM1Ordini(opts: UseOrdiniOptions = {}) {
  return useQuery({
    queryKey: ["m1", "ordini", opts],
    queryFn: () =>
      apiFetch<M1OrdiniListResponse>(`/api/v1/m1/ordini?${buildOrdiniQuery(opts)}`),
    staleTime: 30_000,
  });
}

/**
 * Marca la politica di ritiro di **una commessa**: l'eccezione al cliente.
 *
 * ⚠️ `tassativa` non si può chiedere: si deriva dal ritiro fissato, e l'API la
 * rifiuta con un 422. La risposta porta lo stato **effettivo** che ne risulta,
 * così l'interfaccia non fa credere che la marcatura abbia avuto effetto quando
 * la derivazione vince.
 */
export function useM1RitiroCommessa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ numero, body }: { numero: number; body: RitiroUpdate }) =>
      apiFetch<RitiroApplicato>(`/api/v1/m1/ordini/${numero}/ritiro`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Cambia lo stato della riga, i conteggi del Cruscotto e il lead time per
      // stato: si invalida tutto M1, non il singolo pezzo.
      qc.invalidateQueries({ queryKey: ["m1"] });
    },
  });
}

/**
 * Marca la politica di ritiro di **un cliente**: vale per tutte le sue commesse,
 * comprese quelle che arriveranno. È il gesto che conta — WONDERFOOD ritira a
 * rate come cliente, non commessa per commessa.
 */
export function useM1RitiroCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codice, body }: { codice: number; body: RitiroUpdate }) =>
      apiFetch<RitiroApplicato>(`/api/v1/m1/clienti/${codice}/ritiro`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m1"] }),
  });
}

export function useM1Dashboard() {
  return useQuery({
    queryKey: ["m1", "dashboard"],
    queryFn: () => apiFetch<M1DashboardKPIs>("/api/v1/m1/dashboard"),
    staleTime: 30_000,
  });
}

export function useM1LeadTime() {
  return useQuery({
    queryKey: ["m1", "lead-time"],
    queryFn: () => apiFetch<M1LeadTime>("/api/v1/m1/lead-time"),
    staleTime: 30_000,
  });
}

export function useM1Diagnosi(ordineId: string | null) {
  return useQuery({
    queryKey: ["m1", "diagnosi", ordineId],
    queryFn: () => apiFetch<M1OrdineDiagnosi>(`/api/v1/m1/ordini/${ordineId}/diagnosi`),
    enabled: !!ordineId,
    staleTime: 30_000,
  });
}

export type SpedibiliFinestra = "oggi" | "7gg" | "14gg";

export function useM1Spedibili(finestra: SpedibiliFinestra) {
  return useQuery({
    queryKey: ["m1", "spedibili", finestra],
    queryFn: () =>
      apiFetch<M1SpedibiliResponse>(`/api/v1/m1/spedibili?finestra=${finestra}`),
    staleTime: 30_000,
  });
}

export function useM1ArticoliCritici() {
  return useQuery({
    queryKey: ["m1", "segnali", "articoli-critici"],
    queryFn: () =>
      apiFetch<M1SegnaleArticoloCritico[]>("/api/v1/m1/segnali/articoli-critici"),
    staleTime: 30_000,
  });
}

/** Articoli esclusi dal conteggio perché non sono prodotto finito. */
export function useM1ArticoliEsclusi() {
  return useQuery({
    queryKey: ["m1", "articoli", "esclusi"],
    queryFn: () => apiFetch<ArticoloEscluso[]>("/api/v1/m1/articoli/esclusi"),
    staleTime: 30_000,
  });
}

/**
 * Marca (o rimette) un articolo come materiale di consumo.
 *
 * Sposta i numeri di evadibilità di tutto il modulo — marcando i pallet EPAL le
 * commesse inevadibili passano da 115 a 92 — quindi invalida tutto M1, non solo
 * la lista degli esclusi.
 */
export function useM1ImpostaEsclusione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      codice,
      ...body
    }: {
      codice: string;
      escluso_da_evadibilita: boolean;
      motivo_esclusione?: string | null;
    }) =>
      apiFetch<ArticoloEscluso>(
        `/api/v1/m1/articoli/${encodeURIComponent(codice)}/esclusione`,
        { method: "PATCH", body: JSON.stringify(body) }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m1"] }),
  });
}

/** Chi tiene ferma la merce già pronta: la classifica per cliente (Cruscotto). */
export function useM1ClientiMerceFerma(limit = 8) {
  return useQuery({
    queryKey: ["m1", "clienti-merce-ferma", limit],
    queryFn: () =>
      apiFetch<ClientiMerceFermaResponse>(
        `/api/v1/m1/clienti-merce-ferma?limit=${limit}`
      ),
    staleTime: 30_000,
  });
}

export function useM1StockEccedente() {
  return useQuery({
    queryKey: ["m1", "segnali", "stock-eccedente"],
    queryFn: () =>
      apiFetch<M1SegnaleStockEccedente[]>("/api/v1/m1/segnali/stock-eccedente"),
    staleTime: 30_000,
  });
}
